'use client'

import { useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import Portal from './Portal'
import { usePathname } from 'next/navigation'

// Full-screen service notice for visitors in South Korea.
//
// Geo comes from the same `country` cookie middleware.js already stamps from
// Vercel's `x-vercel-ip-country` (see lib/consent.js getCountry) — there is no
// second lookup and no request of our own. Unknown geo ('ZZ', or the cookie
// missing entirely: local dev, geo failure, or an admin/whitelisted role, which
// middleware deliberately reports as 'ZZ') shows nothing. Failing closed is the
// right default here: a Korean-language interstitial in front of a visitor who
// is not in Korea is worse than a Korean visitor who misses it.
//
// Dismissal is deliberately NOT persisted — no sessionStorage, no cookie, just
// component state. Every full page load shows it again. The state lives here
// rather than in storage so a client-side route change does not re-interrupt
// someone who has already read and closed it: this mounts in the root layout,
// which survives navigation, so "dismissed" holds until the next real reload.
//
// It is a full-viewport interstitial rather than the shared <Modal> because the
// notice has to be read before the site is used, and the dismissal is styled
// down to a small text link for the same reason — the intent is that a visitor
// who can't reach the service takes the hint in the last paragraph (network
// environment) rather than closing past it on reflex. It stays genuinely
// dismissible: the link works, and Escape closes it too.

// Dev-only preview, mirroring lib/consent.js's isConsentDebugForced():
//   localStorage.setItem('pc_force_kr_notice', '1')   // then reload
// (Middleware also honours NEXT_DEV_COUNTRY=KR, which exercises the real path.)
function isDebugForced() {
  if (process.env.NODE_ENV === 'production') return false
  try {
    return !!window.localStorage.getItem('pc_force_kr_notice')
  } catch {
    return false
  }
}

export default function KoreaNoticeModal() {
  // Only Telegram's webview skips the notice. The other chromeless route, the Stories player
  // (/date/), is the site: the notice has to be read there too (2026-10-05 review).
  const pathname = usePathname()
  const chromeless = Boolean(pathname?.startsWith('/telegram/'))
  const [visible, setVisible] = useState(false)
  // Mounted-but-transparent for one frame so the fade-in actually plays.
  const [open, setOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // Telegram Mini App screens are a webview inside another app, not the site.
    if (chromeless || dismissed) return

    const country = Cookies.get('country')
    const inKorea = country?.toUpperCase() === 'KR'
    if (!inKorea && !isDebugForced()) return

    setVisible(true)
    const frame = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(frame)
  }, [chromeless, dismissed])

  const dismiss = () => {
    setDismissed(true)
    setOpen(false)
    // Let the fade-out finish before unmounting.
    setTimeout(() => setVisible(false), 200)
  }

  // Nothing behind the overlay should scroll while it's up. Restores the
  // previous value rather than hard-setting 'unset', so closing this never
  // unlocks a <Modal> that happens to be open underneath.
  useEffect(() => {
    if (!visible) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [visible])

  useEffect(() => {
    if (!visible) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [visible])

  if (!visible) return null

  return (
    <Portal>
      {/* z-[120] sits one tier ABOVE the z-[110] dialog tier that Modal.jsx and
          the drawers share. That is deliberate and is the one exception to the
          "keep all dialog overlays on this tier" note in Modal.jsx: this is not
          a dialog competing within the app, it is a page-level interstitial, and
          it must not end up buried under a modal that auto-opened behind it.
          Opaque page background (not a translucent scrim) so the site is not
          half-readable through it. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="kr-notice-title"
        lang="ko"
        className={`fixed inset-0 z-[120] flex flex-col items-center justify-center overflow-y-auto bg-[var(--background-bg-color)] px-6 py-16 text-center transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="w-full max-w-xl space-y-6">
          <h2
            id="kr-notice-title"
            className="text-2xl font-semibold text-white md:text-3xl"
          >
            대한민국 내 서비스 이용 안내
          </h2>

          <div className="space-y-4 text-base leading-relaxed text-gray-300">
            <p>
              정부의 지역별 접속 제한 조치로 인해 대한민국에서 Promptchan 이용이
              제한될 수 있습니다.
            </p>
            <p>
              현재 관련 내용을 확인하고 있으며, 추가적인 설명을 요청한
              상태입니다. 다른 지원 지역에서는 서비스가 정상적으로 제공됩니다.
            </p>
            <p>
              이용자의 위치 및 네트워크 환경에 따라 접속 가능 여부가 달라질 수
              있습니다.
            </p>
          </div>

          {/* Intentionally understated: small, low-contrast, no button chrome. */}
          <div className="pt-6">
            <button
              type="button"
              onClick={dismiss}
              className="text-xs text-gray-500 underline underline-offset-4 transition-colors hover:text-gray-300 focus:outline-none focus-visible:text-gray-300"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </Portal>
  )
}
